import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Mail, ShieldCheck } from "lucide-react";
import { api, isAdmin } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

export const googleLogin = target => {
  const redirectUrl = window.location.origin + target;
  /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const ForgotForm = ({ email, onBack, testPrefix }) => {
  const [value, setValue] = useState(email || "");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); setBusy(true); setErr("");
    try { setMsg((await api("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: value }) })).message); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };
  return (
    <form className="auth-form" onSubmit={submit} data-testid={`${testPrefix}-forgot-form`}>
      <p className="auth-foot" style={{ margin: 0 }}>Enter your email and we'll send a secure link to set (or reset) your password. Works for Google accounts too.</p>
      <label>Email address<div className="input-icon"><Mail size={15} /><input type="email" required value={value} onChange={e => setValue(e.target.value)} data-testid={`${testPrefix}-forgot-email`} /></div></label>
      {msg && <div className="success-message" data-testid={`${testPrefix}-forgot-success`}>{msg}</div>}
      {err && <div className="error-message" data-testid={`${testPrefix}-forgot-error`}>{err}</div>}
      <button className="button button-gold" disabled={busy} data-testid={`${testPrefix}-forgot-submit`}>{busy ? "Sending…" : "Send me the link"} <ArrowRight size={15} /></button>
      <button type="button" className="link" onClick={onBack} data-testid={`${testPrefix}-forgot-back`}>Back to sign in</button>
    </form>
  );
};

export const LoginForm = ({ redirectTo, admin = false, testPrefix = "login" }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await api("/auth/login", { method: "POST", body: JSON.stringify(form) });
      setUser(u);
      if (admin && !isAdmin(u)) { setErr("This account does not have admin access."); return; }
      nav(redirectTo || (isAdmin(u) ? "/admin" : "/account"), { replace: true });
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  if (forgot) return <ForgotForm email={form.email} onBack={() => setForgot(false)} testPrefix={testPrefix} />;
  const googleOnly = /Google sign-in/i.test(err);
  return (
    <form className="auth-form" onSubmit={submit} data-testid={`${testPrefix}-form`}>
      <label>Email address
        <div className="input-icon"><Mail size={15} /><input type="email" required autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid={`${testPrefix}-email-input`} /></div>
      </label>
      <label>Password
        <div className="input-icon"><Lock size={15} /><input type="password" required autoComplete="current-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid={`${testPrefix}-password-input`} /></div>
      </label>
      {err && (
        <div className="error-message" data-testid={`${testPrefix}-error`}>
          {err}
          {googleOnly && <div style={{ marginTop: 8 }}>Use the <b>Continue with Google</b> button below, or <button type="button" className="link" onClick={() => setForgot(true)} data-testid={`${testPrefix}-set-password-link`}>set a password for this account</button>.</div>}
        </div>
      )}
      <button className="button button-gold" disabled={busy} data-testid={`${testPrefix}-submit-button`}>
        {busy ? "Signing in…" : "Sign in"} <ArrowRight size={15} />
      </button>
      <button type="button" className="link" style={{ justifySelf: "end" }} onClick={() => setForgot(true)} data-testid={`${testPrefix}-forgot-link`}>Forgot password / set a password</button>
      <div className="auth-divider"><span>or</span></div>
      <button type="button" className={googleOnly ? "button button-gold" : "button button-ghost dark"} onClick={() => googleLogin(admin ? "/admin" : "/account")} data-testid={`${testPrefix}-google-button`}>
        <ShieldCheck size={15} /> Continue with Google
      </button>
    </form>
  );
};

export default function LoginPage() {
  return (
    <main className="login-page">
      <div className="login-panel wide">
        <Link to="/" className="brand" data-testid="login-brand"><span className="brand-mark">D</span><span><b>DCEA Hubli–Dharwad</b><small>Member portal</small></span></Link>
        <span className="eyebrow">Member login</span>
        <h1>Welcome <span className="gold-text">back.</span></h1>
        <p>Sign in to view your membership, Member ID, payments and event registrations.</p>
        <LoginForm />
        <p className="auth-foot">New to DCEA? <Link to="/register" data-testid="login-register-link">Create your member account</Link></p>
        <Link to="/" data-testid="login-back-home">Back to public website</Link>
      </div>
    </main>
  );
}
